// Algolia analytics events: storage of search-click attribution +
// lazy-loaded insights sender. Two public event entry points
// (`sendAddedToCart`, `sendViewedObject`) auto-route to the *AfterSearch*
// variant when we have a recent suggestion-click on the same objectID,
// or to the generic variant otherwise.

const STORE_KEY = '_aa_queries';
const STORE_TTL_MS = 60 * 60 * 1000; // 1h — typical browse-then-add window
const USER_TOKEN_KEY = '_aa_uid';

const APP_ID = 'G04SWIXDZX';
const API_KEY = '559cd1fd17b65f114ea78251761bc28c';
const INDEX_PRODUCTS = 'search_feed';
const DEFAULT_CURRENCY = 'PLN';

const readMap = () => {
    try {
        const raw = window.localStorage.getItem(STORE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (e) {
        return {};
    }
};

const writeMap = (map) => {
    try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify(map));
    } catch (e) { /* quota full or storage disabled */ }
};

const prune = (map) => {
    const now = Date.now();
    Object.keys(map).forEach((k) => {
        if (now - (map[k].ts || 0) > STORE_TTL_MS) delete map[k];
    });
    return map;
};

let cachedUserToken = null;
export const getUserToken = () => {
    if (cachedUserToken) return cachedUserToken;
    let uid;
    try {
        uid = window.localStorage.getItem(USER_TOKEN_KEY);
        if (!uid) {
            uid = Math.random().toString(36).slice(2, 14);
            window.localStorage.setItem(USER_TOKEN_KEY, uid);
        }
    } catch (e) {
        uid = Math.random().toString(36).slice(2, 14);
    }
    cachedUserToken = 'user-' + uid;
    return cachedUserToken;
};

export const storeAlgoliaAttribution = (objectId, payload) => {
    if (!objectId || !payload || !payload.queryID || !payload.userToken) return;
    const map = prune(readMap());
    map[String(objectId)] = {
        queryID: payload.queryID,
        userToken: payload.userToken,
        price: typeof payload.price === 'number' ? payload.price : undefined,
        ts: Date.now()
    };
    writeMap(map);
};

export const getAlgoliaAttribution = (objectId) => {
    if (!objectId) return null;
    const map = readMap();
    const entry = map[String(objectId)];
    if (!entry) return null;
    if (Date.now() - (entry.ts || 0) > STORE_TTL_MS) {
        delete map[String(objectId)];
        writeMap(map);
        return null;
    }
    return entry;
};

// Lazy-loaded singleton — fetched on first event send, then cached.
let insightsPromise = null;
const loadInsights = () => {
    if (insightsPromise) return insightsPromise;
    insightsPromise = import(/* webpackChunkName: "algolia-insights" */ '@algolia/client-insights')
        .then((mod) => mod.insightsClient(APP_ID, API_KEY));
    return insightsPromise;
};

const pushEvent = (event) => loadInsights()
    .then((insights) => insights.pushEvents({ events: [event] }))
    .catch(() => { /* swallow analytics errors */ });

// addedToCart — routes to *AfterSearch* variant if we have queryID attribution
// for this objectID, otherwise to the generic addedToCartObjectIDs.
// Pass `quantity`, optional `price` (else read from stored attribution),
// `currency` (default PLN), `discount` for full revenue payload.
export const sendAddedToCart = (objectId, opts = {}) => {
    if (!objectId) return null;
    const id = String(objectId);
    const attribution = getAlgoliaAttribution(id);

    const quantity = opts.quantity > 0 ? opts.quantity : 1;
    const price = typeof opts.price === 'number'
        ? opts.price
        : (attribution && typeof attribution.price === 'number' ? attribution.price : undefined);
    const currency = opts.currency || DEFAULT_CURRENCY;

    const event = {
        eventType: 'conversion',
        eventSubtype: 'addToCart',
        eventName: opts.eventName || (attribution ? 'Product Added To Cart After Search' : 'Product Added To Cart'),
        index: opts.index || INDEX_PRODUCTS,
        userToken: attribution ? attribution.userToken : getUserToken(),
        objectIDs: [id]
    };

    if (attribution) event.queryID = attribution.queryID;

    if (typeof price === 'number') {
        const item = { price, quantity };
        if (typeof opts.discount === 'number') item.discount = opts.discount;
        event.objectData = [item];
        event.value = price * quantity;
        event.currency = currency;
    } else {
        event.objectData = [{ quantity }];
    }

    return pushEvent(event);
};

// viewedObject — routes to viewedObjectIDsAfterSearch when we have attribution
// (a click on this product came from a search-suggestion within TTL), else to
// the generic viewedObjectIDs. Used on product detail page load.
export const sendViewedObject = (objectId, opts = {}) => {
    if (!objectId) return null;
    const id = String(objectId);
    const attribution = getAlgoliaAttribution(id);

    const event = {
        eventType: 'view',
        eventName: opts.eventName || (attribution ? 'Product Viewed After Search' : 'Product Viewed'),
        index: opts.index || INDEX_PRODUCTS,
        userToken: attribution ? attribution.userToken : getUserToken(),
        objectIDs: [id]
    };

    if (attribution) event.queryID = attribution.queryID;

    return pushEvent(event);
};
